import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';

interface HeroCarouselProps {
  movies: Movie[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (movies.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((i) => (i + 1) % movies.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [movies.length]);

  if (movies.length === 0) return null;

  const go = (next: number) => setIndex((next + movies.length) % movies.length);

  return (
    <section className="relative w-full h-[440px] md:h-[560px] lg:h-[620px] overflow-hidden bg-background-100">
      {movies.map((movie, i) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          <div className="absolute inset-0 w-full h-full overflow-hidden">
            <img
              src={movie.banner ?? movie.cover}
              alt={`${movie.title} 焦点图`}
              title={`${movie.title} 高清在线观看`}
              className={`w-full h-full object-cover object-top ${
                i === index ? 'animate-ken-burns' : ''
              }`}
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/80 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-50/40" />

          <div className="relative z-20 h-full px-4 md:px-6 lg:px-10 flex items-center">
            <div className="w-full max-w-2xl">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-semibold">
                  {movie.type === 'tv' ? '热播剧集' : '院线热映'}
                </span>
                <span className="px-3 py-1 rounded-full bg-background-950/60 backdrop-blur text-accent-400 text-xs font-medium">
                  <i className="ri-star-fill mr-1" />
                  {movie.rating.toFixed(1)}
                </span>
                <span className="hidden sm:inline text-xs text-foreground-500">
                  {movie.genres.join(' · ')} · {movie.year}
                </span>
              </div>
              <h2 className="text-3xl md:text-5xl font-bold text-foreground-950 font-heading leading-tight">
                {movie.title}
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed line-clamp-3 max-w-xl">
                {movie.summary}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${movie.id}`}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors"
                >
                  <i className="ri-play-fill text-lg" />
                  立即观看
                </Link>
                <Link
                  to={`/detail/${movie.id}`}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-200/80 backdrop-blur text-foreground-900 text-sm font-medium whitespace-nowrap hover:bg-background-300 transition-colors"
                >
                  <i className="ri-information-line text-lg" />
                  详情介绍
                </Link>
              </div>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={() => go(index - 1)}
        aria-label="上一张"
        className="hidden md:flex absolute z-30 left-4 lg:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-background-950/50 backdrop-blur items-center justify-center text-foreground-900 text-2xl hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line" />
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        aria-label="下一张"
        className="hidden md:flex absolute z-30 right-4 lg:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-background-950/50 backdrop-blur items-center justify-center text-foreground-900 text-2xl hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line" />
      </button>

      <div className="absolute z-30 top-5 right-4 lg:right-6 flex items-center gap-2">
        {movies.map((movie, i) => (
          <button
            key={movie.id}
            type="button"
            aria-label={`切换到 ${movie.title}`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? 'w-8 bg-primary-500' : 'w-4 bg-foreground-700/50 hover:bg-foreground-600'
            }`}
          />
        ))}
      </div>
    </section>
  );
}